










































































































































import moment from 'moment';
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';

import { IQuote, IQuotePackage } from '@/models';
import { ISystemError } from '@/models/error/index';
import { createPdf, setErrorFromAxiosError } from '@/utils/helpers';

import mixins from '@/utils/mixins';
import authMixin from '@/mixins/auth';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import QuoteDocumentPackageForm from '@/components/quotes/QuoteDocumentPackageForm.vue';
import QuoteDocumentPackageBuilder from '@/components/quotes/QuoteDocumentPackageBuilder.vue';
import QuoteValidityExiprationDialog from '@/components/quotes/QuoteValidityExiprationDialog.vue';
import ConfirmationDialog from '../common/ConfirmationDialog.vue';
import { AxiosError } from 'axios';
import QuoteLanguageSelector from './QuoteLanguageSelector.vue';

interface Options extends InstanceType<typeof Vue> {
  $refs: {
    documentPackageForm: InstanceType<typeof QuoteDocumentPackageForm>;
    quoteValidityExiprationDialog: InstanceType<
      typeof QuoteValidityExiprationDialog
    >;
  };
}

/**
 * @summary When updating STEPS update {FirstStep} if other enum is made 1
 * @enum STEPS
 */
enum STEPS {
  QuoteSelector = 1,
  PackageBuilder = 2
}

export default mixins(authMixin)
  .extend<Options>({ functional: false })
  .extend({
    name: 'QuoteDocumentBuilderDialog',

    components: {
      ALDialog,
      ErrorAlert,
      QuoteDocumentPackageForm,
      QuoteDocumentPackageBuilder,
      QuoteValidityExiprationDialog,
      ConfirmationDialog,
      QuoteLanguageSelector
    },

    data() {
      return {
        STEPS,
        dialog: false,
        loading: false,

        error: null as ISystemError | null,
        step: STEPS.QuoteSelector,
        quotePackage: null as null | IQuotePackage,
        quote: null as null | IQuote,

        exportLoading: false,
        packageFormLoading: false,

        quotePackageLanguageExportDialog: false
      };
    },

    computed: {
      ...mapGetters({
        getQuotePackageByQuoteNumberAndRevision:
          'quote/getQuotePackageByQuoteNumberAndRevision',
        getQuotePackageById: 'quote/getQuotePackageById'
      })
    },

    methods: {
      ...mapActions({
        fetchQuotePackage: 'quote/fetchQuotePackage',
        exportQuotePackage: 'quote/exportQuotePackage'
      }),

      async open(quote: IQuote) {
        this.clearErrors();

        this.dialog = true;

        this.loading = true;
        this.quote = quote;
        this.quotePackage = await this.tryGetOrFetchQuotePackageByQuoteId(
          quote.number!,
          quote.revision!
        );

        // If either the machine or ventilation quote is not set for the package builder then open into the QuoteSelector step
        this.step =
          this.quotePackage && this.quotePackage.quotes.length > 1
            ? STEPS.PackageBuilder
            : STEPS.QuoteSelector;

        this.loading = false;
      },

      async tryGetOrFetchQuotePackageByQuoteId(
        quoteNumber: number,
        revision: number
      ): Promise<IQuotePackage | null> {
        if (
          !this.getQuotePackageByQuoteNumberAndRevision(quoteNumber, revision)
        ) {
          if (this.quote!.package) {
            try {
              await this.fetchQuotePackage(this.quote!.package);
            } catch (e) {
              this.error = setErrorFromAxiosError(e as AxiosError);
            }
          }
        }

        return (
          this.getQuotePackageByQuoteNumberAndRevision(quoteNumber, revision) ??
          null
        );
      },

      setError(error: ISystemError) {
        this.error = error;
      },

      setQuotePackage(quotePackage: IQuotePackage) {
        this.quotePackage = quotePackage;
      },

      moveQuotePackageFormToPackageBuilder(quotePackage: IQuotePackage) {
        this.setQuotePackage(quotePackage);
        this.moveToPackageBuilder();
      },

      moveToQuoteSelector() {
        this.step = STEPS.QuoteSelector;
      },
      moveToPackageBuilder() {
        this.step = STEPS.PackageBuilder;
      },

      close() {
        this.dialog = false;
      },

      clearErrors() {
        this.error = null;
      },

      savePackageForm() {
        return this.$refs.documentPackageForm.saveQuotePackage();
      },

      async quotePackageLanguageExportDialogClickListener(
        event: MouseEvent,
        listeners: Record<string, (event?: MouseEvent) => void>
      ) {
        if (
          await this.$refs.quoteValidityExiprationDialog.openIfQuoteExpired()
        ) {
          return listeners.click(event);
        }
      },

      exportQuotePackagePDF(language: string) {
        this.clearErrors();
        this.exportLoading = true;
        this.exportQuotePackage({
          id: this.quotePackage!.id,
          language: language
        })
          .then((data: Blob) => {
            createPdf(
              data,
              `quote-package-${moment(new Date()).format(
                'YYYY-MM-DD-HH-mm-ss'
              )}`
            );
            this.close();
          })
          .catch(error => {
            this.error = setErrorFromAxiosError(error);
          })
          .finally(() => {
            this.exportLoading = false;
          });
      }
    }
  });
